/**
 * Display formatting for the tenancy surfaces. Business dates stay `YYYY-MM-DD` everywhere in
 * the code (PRD §6.6) and become readable only here, at the very edge.
 */

import i18n from "@/i18n";
import { isBusinessDate, parseBusinessDate, type BusinessDate } from "@/shared/dates";
import { BUNDESLAND_LABELS } from "@/shared/holidays";
import { shareToPercent } from "@/shared/schemas/tenancy";

export { formatPersonName } from "@/shared/person-name";
export { BUNDESLAND_LABELS };

export function bundeslandLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return code in BUNDESLAND_LABELS
    ? BUNDESLAND_LABELS[code as keyof typeof BUNDESLAND_LABELS]
    : code;
}

function displayLocale(locale?: string): string {
  if (locale) return locale;
  return i18n.language === "en" ? "en-GB" : "de-DE";
}

/** A business date the way the current locale writes it; the empty date renders as a dash. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return "—";
  if (!isBusinessDate(date)) return date;
  const parsed = parseBusinessDate(date as BusinessDate);
  return new Intl.DateTimeFormat(displayLocale(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

/** Month + year for card subtitles (e.g. "August 2026" / "August 2026"). */
export function formatMonthYear(date: string | null | undefined): string {
  if (!date) return "—";
  if (!isBusinessDate(date)) return date;
  const parsed = parseBusinessDate(date as BusinessDate);
  return new Intl.DateTimeFormat(displayLocale(), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

/**
 * A project's term as an inline phrase ("vom 01.08.2026 bis 01.08.2027"), for sentences that
 * explain why a year holds no data.
 */
export function formatProjectTerm(startDate: string, endDate: string): string {
  return i18n.t("adminExtra.termRange", { from: formatDate(startDate), to: formatDate(endDate) });
}

/** The same term as a standalone caption under a project picker. */
export function formatProjectTermLine(startDate: string, endDate: string): string {
  return i18n.t("adminExtra.projectTerm", { from: formatDate(startDate), to: formatDate(endDate) });
}

/** Short weekday label for a business date (calendar / export columns). */
export function formatWeekdayShort(date: BusinessDate, locale?: string): string {
  return new Intl.DateTimeFormat(displayLocale(locale), {
    weekday: "short",
    timeZone: "UTC",
  }).format(parseBusinessDate(date));
}

/**
 * Weekday + locale date for toast copy (ticket 132), e.g. DE `Donnerstag, 12.09.2026`
 * / EN `Thursday, 12/09/2026`. Same locale / UTC / business-date rules as `formatDate`.
 */
export function formatWeekdayDate(date: string | null | undefined, locale?: string): string {
  if (!date) return "—";
  if (!isBusinessDate(date)) return date;
  const parsed = parseBusinessDate(date as BusinessDate);
  return new Intl.DateTimeFormat(displayLocale(locale), {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

/** A share (0…1) as the percentage the UI and the import sheet speak (PLAN.md D-28). */
export function formatPercent(share: number): string {
  return `${shareToPercent(share)}%`;
}

export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return "—";
  return new Intl.NumberFormat(displayLocale(), {
    maximumFractionDigits: 2,
  }).format(hours);
}
